/**
 * Quality tiers and the settings object every renderer subsystem reads.
 *
 * The tier is a *typed config object*, not a number sprinkled through the codebase: clouds,
 * shadows, terrain, water, post and effects each read their own slice, so a new subsystem
 * adds a field here and immediately participates in the Auto governor.
 *
 * Reference targets (CLAUDE.md): 60 fps floor at Medium on an M1 Air / Iris Xe class laptop,
 * 120+ fps at High on a discrete GPU. Ship `auto` as the default.
 */

import { Emitter } from './events.js';

export const TIERS = ['low', 'medium', 'high', 'ultra'] as const;
export type QualityTier = (typeof TIERS)[number];

/** `auto` lets the governor pick; anything else pins the tier. */
export type QualityMode = 'auto' | QualityTier;

export interface ShadowSettings {
  enabled: boolean;
  /**
   * Shadow map resolution **per cascade** [px].
   *
   * Per cascade, not in total, which is why Ultra is 2048 rather than the 4096 it was before
   * cascades were real: four 4096² depth maps is 268 MB of VRAM for a shadow set whose casters
   * are one aeroplane and some runway pavement. Four at 2048² is 67 MB and indistinguishable.
   */
  mapSize: number;
  /** Cascade count for the CSM split. */
  cascades: number;
  /** Distance the last cascade reaches [m]. */
  distanceM: number;
  /** PCF radius [texels]; 0 = hard shadows. */
  softnessTexels: number;
}

export interface CloudSettings {
  /**
   * `off` = clear sky, `volumetric` = raymarched, `billboard` = sprite cumulus.
   *
   * `billboard` is declared but **not implemented** and nothing selects it; it is the slot the
   * impostor fallback would land in. Selecting it renders no sky at all.
   */
  mode: 'off' | 'billboard' | 'volumetric';
  /** Render scale of the cloud buffer relative to the main framebuffer. */
  resolutionScale: number;
  /** Raymarch steps through the cloud layer. */
  raymarchSteps: number;
  /** Reproject the previous frame's cloud buffer (needs a motion vector pass). */
  temporalReprojection: boolean;
  /** Clouds cast shadows onto the terrain. */
  castShadows: boolean;
}

export interface TerrainSettings {
  /**
   * Zoom-level offset applied to the deepest LOD the quadtree will refine to.
   *
   * Negative coarsens. Positive has no effect: the tile providers only publish to a fixed
   * depth, and refining past it just requests tiles that do not exist. See `quadtree.ts`.
   */
  lodBias: number;
  /** Simultaneous tile fetches. CLAUDE.md caps this at 8 to stay a polite client. */
  maxConcurrentTileLoads: number;
  /** LRU ceiling for decoded tiles [MB]. */
  tileCacheMB: number;
  /**
   * Screen-space geometric error target [px] — smaller means more triangles and more tiles.
   *
   * These are not free-floating quality knobs; they are solved against the budgets in
   * CLAUDE.md. Tiles per LOD ring works out to `3π·pixelScale² / ((gridSize−1)²·target²)`,
   * independent of zoom, so at 1080p with a 65-vertex grid the whole visible set is roughly
   * `39 · rings` tiles at 8 px — about 200 tiles and 1.7 M triangles at Medium, inside both the
   * 350 draw calls and the 2.5 M triangles the spec allows. Halving the target quadruples the
   * tile count, so these move in small steps and get re-measured when they do.
   */
  geometryErrorPx: number;
  /** Near-field detail textures on top of the satellite imagery. */
  detailTextures: boolean;
  /** Triplanar projection so cliffs do not smear. */
  triplanar: boolean;
  /**
   * Finished tiles adopted into the scene per frame.
   *
   * The streaming throttle. Workers deliver in bursts — a dozen tiles can land between two
   * frames — and adopting a burst all at once puts a dozen buffer creations, texture uploads and
   * first-use pipeline lookups into one frame, which is a 40 ms hole in an otherwise flat frame
   * rate. Spreading them costs a few frames of latency behind a fetch that already took
   * hundreds of milliseconds, and nobody can see it.
   */
  tileUploadsPerFrame: number;
}

export interface WaterSettings {
  /** `flat` = normal-mapped plane, `gerstner` = displaced swell. */
  mode: 'flat' | 'gerstner';
  gerstnerWaves: number;
  screenSpaceReflections: boolean;
}

export interface PostSettings {
  bloom: boolean;
  /** Temporal reprojection AA. */
  taa: boolean;
  /** MSAA sample count when TAA is off (1 = none). */
  msaaSamples: number;
  autoExposure: boolean;
  motionBlur: boolean;
  ssao: boolean;
}

export interface EffectSettings {
  /** Live particle ceiling across all emitters. */
  particleBudget: number;
  contrails: boolean;
  /** Tracers emit real point lights rather than just glowing sprites. */
  tracerLights: boolean;
  heatShimmer: boolean;
  wingtipVortices: boolean;
}

export interface QualitySettings {
  tier: QualityTier;
  /** Internal render resolution multiplier (before the device pixel ratio cap). */
  renderScale: number;
  /** Hard cap on device pixel ratio — the single biggest fill-rate lever on retina panels. */
  maxPixelRatio: number;
  /** Far plane / terrain draw distance [m]. */
  drawDistanceM: number;
  /** Texture anisotropy; 1 = off. */
  anisotropy: number;
  shadows: ShadowSettings;
  clouds: CloudSettings;
  terrain: TerrainSettings;
  water: WaterSettings;
  post: PostSettings;
  effects: EffectSettings;
}

export const QUALITY_PRESETS: Readonly<Record<QualityTier, QualitySettings>> = Object.freeze({
  low: {
    tier: 'low',
    renderScale: 0.75,
    maxPixelRatio: 1,
    drawDistanceM: 40_000,
    anisotropy: 1,
    shadows: { enabled: false, mapSize: 1024, cascades: 1, distanceM: 500, softnessTexels: 0 },
    // Volumetric even at Low, at a sixth of Medium's cost (16 steps into a 40 %-scale buffer).
    // `billboard` is still in the union for the impostor path, but nothing selects it: it has no
    // implementation, so a tier that asked for it rendered an empty sky — and Low is exactly the
    // tier the governor drops a struggling machine to, so that was the sky most likely to be seen.
    clouds: { mode: 'volumetric', resolutionScale: 0.4, raymarchSteps: 16, temporalReprojection: true, castShadows: false },
    /*
     * `detailTextures` and `triplanar` are ON at Low, which looks like a mistake and is not.
     *
     * They are **uniforms multiplied into the result**, not branches — see `terrainMaterial.ts`,
     * where `detailAmount` and `triplanarAmount` scale a value the shader has already computed. A
     * fragment shader has no branch there and a uniform cannot be constant-folded, so all four
     * `detailNoise` evaluations run whether the flags are on or off. Turning them off saved
     * **zero GPU work** and only removed the surface grain — on the one tier whose imagery is
     * coarsest, because `lodBias: -1` also takes it a level down to ~15 m/px.
     *
     * `lodBias` stays at −1: that one buys real work back (fewer, coarser tiles to stream, mesh
     * and draw), which is the trade this tier exists to make.
     */
    terrain: { lodBias: -1, maxConcurrentTileLoads: 4, tileCacheMB: 110, geometryErrorPx: 12, detailTextures: true, triplanar: true, tileUploadsPerFrame: 1 },
    water: { mode: 'flat', gerstnerWaves: 0, screenSpaceReflections: false },
    post: { bloom: false, taa: false, msaaSamples: 1, autoExposure: false, motionBlur: false, ssao: false },
    effects: { particleBudget: 256, contrails: false, tracerLights: false, heatShimmer: false, wingtipVortices: false },
  },
  medium: {
    tier: 'medium',
    renderScale: 1,
    maxPixelRatio: 1.5,
    drawDistanceM: 80_000,
    anisotropy: 4,
    shadows: { enabled: true, mapSize: 1024, cascades: 2, distanceM: 1_500, softnessTexels: 1 },
    clouds: { mode: 'volumetric', resolutionScale: 0.5, raymarchSteps: 48, temporalReprojection: true, castShadows: false },
    terrain: { lodBias: 0, maxConcurrentTileLoads: 6, tileCacheMB: 200, geometryErrorPx: 9.5, detailTextures: true, triplanar: true, tileUploadsPerFrame: 2 },
    water: { mode: 'gerstner', gerstnerWaves: 4, screenSpaceReflections: false },
    post: { bloom: true, taa: true, msaaSamples: 1, autoExposure: true, motionBlur: false, ssao: false },
    effects: { particleBudget: 1024, contrails: true, tracerLights: false, heatShimmer: true, wingtipVortices: true },
  },
  high: {
    tier: 'high',
    renderScale: 1,
    maxPixelRatio: 2,
    drawDistanceM: 140_000,
    anisotropy: 8,
    shadows: { enabled: true, mapSize: 2048, cascades: 3, distanceM: 4_000, softnessTexels: 2 },
    clouds: { mode: 'volumetric', resolutionScale: 0.5, raymarchSteps: 96, temporalReprojection: true, castShadows: true },
    terrain: { lodBias: 0, maxConcurrentTileLoads: 8, tileCacheMB: 250, geometryErrorPx: 9, detailTextures: true, triplanar: true, tileUploadsPerFrame: 3 },
    water: { mode: 'gerstner', gerstnerWaves: 6, screenSpaceReflections: true },
    post: { bloom: true, taa: true, msaaSamples: 1, autoExposure: true, motionBlur: true, ssao: true },
    effects: { particleBudget: 4096, contrails: true, tracerLights: true, heatShimmer: true, wingtipVortices: true },
  },
  ultra: {
    tier: 'ultra',
    renderScale: 1,
    maxPixelRatio: 2,
    drawDistanceM: 200_000,
    anisotropy: 16,
    shadows: { enabled: true, mapSize: 2_048, cascades: 4, distanceM: 8_000, softnessTexels: 3 },
    clouds: { mode: 'volumetric', resolutionScale: 1, raymarchSteps: 160, temporalReprojection: true, castShadows: true },
    terrain: { lodBias: 0, maxConcurrentTileLoads: 8, tileCacheMB: 250, geometryErrorPx: 9, detailTextures: true, triplanar: true, tileUploadsPerFrame: 3 },
    water: { mode: 'gerstner', gerstnerWaves: 8, screenSpaceReflections: true },
    post: { bloom: true, taa: true, msaaSamples: 1, autoExposure: true, motionBlur: true, ssao: true },
    effects: { particleBudget: 8192, contrails: true, tracerLights: true, heatShimmer: true, wingtipVortices: true },
  },
});

export interface QualityChangeEvent {
  tier: QualityTier;
  previousTier: QualityTier;
  settings: QualitySettings;
  /** Why the tier moved — `governor` means the Auto rules fired. */
  reason: 'user' | 'governor' | 'init';
}

export interface QualityEvents extends Record<string, unknown> {
  change: QualityChangeEvent;
  modeChange: { mode: QualityMode };
}

const STORAGE_KEY = 'skysim.quality';

/** Index of a tier in {@link TIERS}; -1 if unknown. */
export function tierIndex(tier: QualityTier): number {
  return TIERS.indexOf(tier);
}

/**
 * Owns the active tier and broadcasts changes.
 *
 * `updateFixed` / `updateFrame`: neither. This is a state holder poked by the governor
 * (a few times a minute at most) and by the settings UI.
 */
export class QualityManager {
  readonly events = new Emitter<QualityEvents>();

  private modeValue: QualityMode = 'auto';
  private tierValue: QualityTier;

  constructor(initialMode: QualityMode = 'auto', autoStartTier: QualityTier = 'medium') {
    this.modeValue = initialMode;
    this.tierValue = initialMode === 'auto' ? autoStartTier : initialMode;
  }

  get mode(): QualityMode {
    return this.modeValue;
  }

  get tier(): QualityTier {
    return this.tierValue;
  }

  get settings(): QualitySettings {
    return QUALITY_PRESETS[this.tierValue];
  }

  /** True when the governor is allowed to move the tier. */
  get isAuto(): boolean {
    return this.modeValue === 'auto';
  }

  setMode(mode: QualityMode): void {
    if (mode === this.modeValue) return;
    this.modeValue = mode;
    this.events.emit('modeChange', { mode });
    if (mode !== 'auto') this.setTier(mode, 'user');
  }

  setTier(tier: QualityTier, reason: QualityChangeEvent['reason'] = 'user'): void {
    if (tier === this.tierValue) return;
    const previousTier = this.tierValue;
    this.tierValue = tier;
    this.events.emit('change', { tier, previousTier, settings: this.settings, reason });
  }

  canStepDown(): boolean {
    return tierIndex(this.tierValue) > 0;
  }

  canStepUp(): boolean {
    return tierIndex(this.tierValue) < TIERS.length - 1;
  }

  /** Drop one tier. Returns false when already at Low. */
  stepDown(reason: QualityChangeEvent['reason'] = 'governor'): boolean {
    if (!this.canStepDown()) return false;
    this.setTier(TIERS[tierIndex(this.tierValue) - 1], reason);
    return true;
  }

  /** Raise one tier. Returns false when already at Ultra. */
  stepUp(reason: QualityChangeEvent['reason'] = 'governor'): boolean {
    if (!this.canStepUp()) return false;
    this.setTier(TIERS[tierIndex(this.tierValue) + 1], reason);
    return true;
  }

  /** Persist mode + tier so a returning player keeps their settings. No-op without storage. */
  save(storage: Storage | undefined = globalThis.localStorage): void {
    try {
      storage?.setItem(STORAGE_KEY, JSON.stringify({ mode: this.modeValue, tier: this.tierValue }));
    } catch {
      // Private mode / disabled storage: preferences are simply not sticky.
    }
  }

  /** Restore what {@link save} wrote. Returns true when something was applied. */
  load(storage: Storage | undefined = globalThis.localStorage): boolean {
    try {
      const raw = storage?.getItem(STORAGE_KEY);
      if (!raw) return false;
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null) return false;
      const record = parsed as { mode?: unknown; tier?: unknown };
      const tier = TIERS.find((t) => t === record.tier);
      const mode = record.mode === 'auto' ? 'auto' : TIERS.find((t) => t === record.mode);
      if (tier) this.setTier(tier, 'init');
      if (mode) {
        this.modeValue = mode;
        this.events.emit('modeChange', { mode });
      }
      return Boolean(tier ?? mode);
    } catch {
      return false;
    }
  }
}
